import type { Brand, RulesEntities } from '@jane/business-admin/types';
import type {
  ConditionOption,
  SelectConditionSection,
} from '@jane/shared-b2b/components';

/** The rules key the brands condition saves under */
export const BRANDS_KEY = 'brand_ids';

const toFetchedOption = ({
  logoUrl,
  name,
  productBrandId,
}: Brand): ConditionOption => ({
  label: name,
  logo: logoUrl ?? undefined,
  value: String(productBrandId),
});

/**
 * A selection the catalog has not fetched, named by whatever else knows it. An
 * id nothing names still reads as an option rather than being dropped: the
 * section rebuilds `brand_ids` from its own items, so leaving one out would
 * quietly remove that brand from the special on the next save.
 */
const toNamedOption = (
  productBrandId: number,
  named: RulesEntities['brands']
): ConditionOption => ({
  label: named?.[productBrandId]?.name ?? `Brand ${productBrandId}`,
  logo: named?.[productBrandId]?.logo_url,
  value: String(productBrandId),
});

/**
 * A fetched page as a name dictionary, shaped the way a special names the
 * brands its rules reference, so what a page surfaced and what the special
 * carried read as one lookup.
 */
export const toBrandNames = (
  catalog: Brand[] = []
): NonNullable<RulesEntities['brands']> =>
  Object.fromEntries(
    catalog.map(({ logoUrl, name, productBrandId }) => [
      productBrandId,
      { logo_url: logoUrl ?? undefined, name },
    ])
  );

export interface ToBrandsSectionArgs {
  /** The brands the catalog has fetched, for the current query and pages */
  catalog: Brand[];
  /** Names and logos for brands the catalog has not fetched, keyed by product brand id */
  namedBrands: RulesEntities['brands'];
  /** Asks the catalog for its next page */
  onEndReached(): void;
  /** Hands the catalog a new search query */
  onSearch(query: string): void;
  /** Product brand ids every group has selected, included and excluded */
  selectedIds: number[];
}

/**
 * The brands condition as one section. `items` is the fetched catalog with
 * every selected brand ahead of it — a selection the catalog has not reached
 * would otherwise lose its tag and, worse, be dropped by the minimal cover the
 * next edit writes.
 */
export const toBrandsSection = ({
  catalog,
  namedBrands,
  onEndReached,
  onSearch,
  selectedIds,
}: ToBrandsSectionArgs): SelectConditionSection => {
  const selected = new Set(selectedIds);
  const fetched = new Map(
    catalog.map((brand) => [brand.productBrandId, brand])
  );

  return {
    allowExcludes: true,
    items: [
      ...selectedIds.map((id) => {
        const brand = fetched.get(id);

        return brand ? toFetchedOption(brand) : toNamedOption(id, namedBrands);
      }),
      ...catalog
        .filter(({ productBrandId }) => !selected.has(productBrandId))
        .map(toFetchedOption),
    ],
    key: BRANDS_KEY,
    label: 'Brands',
    modalVariant: 'flex',
    onEndReached,
    onSearch,
    type: 'multiselect',
  };
};
